import { useEffect, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, ArrowLeft, Check, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { adminButton } from '@/components/admin/ui';
import { AiUpgradePrompt } from '@/components/admin/AiUpgradePrompt';
import {
  useAiGenerateLogo,
  useBrandPrimaryColour,
  useUploadAiLogo,
  type AiLogoOption,
  type AiLogoStyle,
  type AiFailure,
} from '@/hooks/useAiFeatures';
import { lowestPlanWith } from '@/lib/plan-limits';
import { rasteriseSvgToPng } from '@/lib/svg-raster';
import { cn } from '@/lib/utils';
import { svgDataUrl } from '../../../supabase/functions/_shared/svg-sanitize';

const STYLES: { value: AiLogoStyle; label: string; hint: string }[] = [
  { value: 'modern', label: 'Modern', hint: 'Clean shapes, confident lettering' },
  { value: 'friendly', label: 'Friendly', hint: 'Rounded and approachable' },
  { value: 'premium', label: 'Premium', hint: 'Refined and understated' },
  { value: 'minimal', label: 'Minimal', hint: 'As simple as it gets' },
];

const HEX = /^#[0-9a-f]{6}$/i;
const DEFAULT_MAIN = '#0f766e';
const DEFAULT_ACCENT = '#1f2937';

const logoSchema = z.object({
  businessName: z.string().trim().min(1, 'Add your business name').max(60, 'Keep it under 60 characters'),
  style: z.enum(['modern', 'friendly', 'premium', 'minimal']),
  mainColour: z.string().regex(HEX, 'Pick a colour'),
  accentColour: z.string().regex(HEX, 'Pick a colour'),
  iconIdea: z.string().trim().max(120, 'Keep it under 120 characters'),
});
type LogoValues = z.infer<typeof logoSchema>;

/** Previews are always <img> sources (data: URLs), never injected markup. */
function previewSrc(logo: AiLogoOption): string {
  return logo.kind === 'image' ? logo.image : svgDataUrl(logo.wide_svg);
}

/**
 * "Create a logo with AI" on Company Info (Team and Business). The
 * ai-generate-logo function returns three options: pictures by default (the
 * browser has already removed their white background, see useAiGenerateLogo),
 * or sanitised SVGs when the function runs in SVG mode. Each is shown as an
 * <img> on a light and a dark background. The chosen one is uploaded as a
 * transparent logo.png (plus logo.svg in SVG mode), and the PNG becomes the
 * company logo through `onSave`.
 */
export function AiLogoMakerDialog({
  open,
  onOpenChange,
  orgId,
  allowed,
  defaultName,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string | null;
  /** From the plan limits; undefined while loading. */
  allowed: boolean | undefined;
  defaultName: string;
  onSave: (logoUrl: string) => Promise<void>;
}) {
  const generate = useAiGenerateLogo();
  const upload = useUploadAiLogo();
  const { data: brandColour } = useBrandPrimaryColour(orgId, open && allowed !== false);
  const [options, setOptions] = useState<AiLogoOption[] | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [shown, setShown] = useState<string[]>([]);

  const form = useForm<LogoValues>({
    resolver: zodResolver(logoSchema),
    defaultValues: { businessName: defaultName, style: 'modern', mainColour: DEFAULT_MAIN, accentColour: DEFAULT_ACCENT, iconIdea: '' },
  });

  // Prefill the name and the site's main colour each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    if (!form.formState.dirtyFields.businessName) form.setValue('businessName', defaultName);
    if (brandColour && !form.formState.dirtyFields.mainColour) form.setValue('mainColour', brandColour);
  }, [open, defaultName, brandColour, form]);

  useEffect(() => {
    if (!open) {
      setProblem(null);
      setSaving(false);
    }
  }, [open]);

  const busy = generate.isPending || saving;

  const run = async (values: LogoValues, avoid: string[]) => {
    if (!orgId) return;
    setProblem(null);
    const result = await generate.mutateAsync({
      organizationId: orgId,
      businessName: values.businessName,
      style: values.style,
      colours: [values.mainColour, values.accentColour],
      iconIdea: values.iconIdea,
      avoid,
    });
    if (!result.ok) {
      setProblem((result as AiFailure).message);
      return;
    }
    setOptions(result.logos);
    setSelected(null);
    setShown((prev) => [...prev, ...result.logos.map((l) => `${l.name}: ${l.concept}`)].slice(-6));
  };

  const onGenerate = form.handleSubmit((values) => run(values, []));
  const onTryAgain = form.handleSubmit((values) => run(values, shown));

  const onUse = async () => {
    if (!orgId || selected === null || !options?.[selected]) return;
    const choice = options[selected];
    setSaving(true);
    setProblem(null);
    try {
      const { logoUrl } =
        choice.kind === 'image'
          ? await upload.mutateAsync({ orgId, png: choice.png })
          : await upload.mutateAsync({
              orgId,
              svg: choice.wide_svg,
              png: await rasteriseSvgToPng(choice.wide_svg),
              squareSvg: choice.square_svg,
            });
      await onSave(logoUrl);
      setOptions(null);
      setSelected(null);
      setShown([]);
      onOpenChange(false);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'We couldn’t save that logo. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
            Create a logo with AI
          </DialogTitle>
          <DialogDescription>
            Tell us the feel you’re after and we’ll design three simple logos to choose from. Nothing changes until you
            pick one and save it.
          </DialogDescription>
        </DialogHeader>

        {allowed === false ? (
          <AiUpgradePrompt feature="AI logo maker" plan={lowestPlanWith('aiLogoMaker')}>
            Get three logo ideas in your colours in under a minute, and use your favourite across your site.
          </AiUpgradePrompt>
        ) : options ? (
          <div className="space-y-3">
            <div role="radiogroup" aria-label="Logo options" className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {options.map((logo, index) => (
                <button
                  key={`${logo.name}-${index}`}
                  type="button"
                  role="radio"
                  aria-checked={selected === index}
                  aria-label={`Logo option ${index + 1}: ${logo.name}`}
                  onClick={() => setSelected(index)}
                  disabled={busy}
                  data-testid={`ai-logo-option-${index}`}
                  className={cn(
                    'group relative flex flex-col gap-2 rounded-xl border bg-card p-2.5 text-left transition-colors',
                    selected === index ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary/40',
                  )}
                >
                  {/* The same logo on a light and a dark background (fixed colours in both themes), so owners can judge both. */}
                  <span className="grid w-full grid-cols-1 gap-1.5">
                    {(['light', 'dark'] as const).map((tone) => (
                      <span
                        key={tone}
                        data-testid={`ai-logo-option-${index}-${tone}`}
                        className={cn(
                          'flex aspect-[2/1] w-full items-center justify-center rounded-lg p-3',
                          tone === 'light' ? 'bg-white' : 'bg-zinc-900',
                        )}
                      >
                        <img src={previewSrc(logo)} alt="" className="h-full w-full object-contain" />
                      </span>
                    ))}
                  </span>
                  <span className="flex items-center gap-2">
                    {logo.kind === 'svg' && logo.square_svg && (
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white p-1">
                        <img src={svgDataUrl(logo.square_svg)} alt="" className="h-full w-full object-contain" />
                      </span>
                    )}
                    <span className="min-w-0">
                      <span className="block text-[13px] font-semibold text-foreground">{logo.name}</span>
                      {logo.concept && <span className="block text-[12px] text-muted-foreground">{logo.concept}</span>}
                    </span>
                  </span>
                  {selected === index && (
                    <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <Check className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                  )}
                </button>
              ))}
            </div>
            <p className="text-[12px] text-muted-foreground">
              {options.some((logo) => logo.kind === 'svg')
                ? 'We save it as a sharp SVG and a PNG, and use the PNG on your site and emails.'
                : 'We’ve taken out the white background, so it sits cleanly on your site and emails.'}
            </p>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={onGenerate} className="adm-form grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="businessName"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>Business name</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" disabled={busy} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="style"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>Style</FormLabel>
                    <div role="radiogroup" aria-label="Style" className="grid grid-cols-2 gap-2 md:grid-cols-4">
                      {STYLES.map((style) => (
                        <button
                          key={style.value}
                          type="button"
                          role="radio"
                          aria-checked={field.value === style.value}
                          disabled={busy}
                          onClick={() => field.onChange(style.value)}
                          className={cn(
                            'rounded-lg border p-2.5 text-left transition-colors',
                            field.value === style.value ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-border bg-card hover:bg-muted',
                          )}
                        >
                          <span className="block text-[13px] font-semibold text-foreground">{style.label}</span>
                          <span className="block text-[11.5px] text-muted-foreground">{style.hint}</span>
                        </button>
                      ))}
                    </div>
                  </FormItem>
                )}
              />
              <ColourField name="mainColour" label="Main colour" form={form} disabled={busy} hint={brandColour ? 'From your site theme' : undefined} />
              <ColourField name="accentColour" label="Second colour" form={form} disabled={busy} />
              <FormField
                control={form.control}
                name="iconIdea"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>Icon idea (optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. a house, a leaf, a sparkle, a mop" autoComplete="off" disabled={busy} {...field} />
                    </FormControl>
                    <FormDescription>Leave blank and we’ll suggest something.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        )}

        {generate.isPending && (
          <p
            role="status"
            className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 p-2.5 text-[13px] text-foreground"
            data-testid="ai-logo-generating"
          >
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" aria-hidden="true" />
            Designing three logo ideas… this takes about 30 seconds.
          </p>
        )}

        {problem && (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-border bg-muted/50 p-2.5 text-[13px] text-foreground"
            data-testid="ai-logo-problem"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            {problem}
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {allowed === false ? (
            <Button type="button" size="sm" variant="ghost" className={adminButton.outline} onClick={() => onOpenChange(false)}>
              Close
            </Button>
          ) : options ? (
            <>
              <Button key="back" type="button" size="sm" variant="ghost" className={adminButton.ghost} disabled={busy} onClick={() => setOptions(null)}>
                <ArrowLeft />
                Change details
              </Button>
              <Button key="again" type="button" size="sm" variant="ghost" className={adminButton.outline} disabled={busy} onClick={onTryAgain}>
                {generate.isPending ? <Loader2 className="animate-spin" /> : <RefreshCw />}
                Try again
              </Button>
              <Button key="use" type="button" size="sm" className={adminButton.primary} disabled={busy || selected === null} onClick={onUse}>
                {saving ? <Loader2 className="animate-spin" /> : <Check />}
                {saving ? 'Saving…' : 'Use this logo'}
              </Button>
            </>
          ) : (
            <Button
              key="generate"
              type="button"
              size="sm"
              className={adminButton.primary}
              onClick={onGenerate}
              disabled={busy || !orgId || allowed === undefined}
            >
              {generate.isPending ? <Loader2 className="animate-spin" /> : <Sparkles />}
              {generate.isPending ? 'Designing…' : 'Design my logos'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ColourField({
  name,
  label,
  form,
  disabled,
  hint,
}: {
  name: 'mainColour' | 'accentColour';
  label: string;
  form: UseFormReturn<LogoValues>;
  disabled: boolean;
  hint?: string;
}) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <div className="flex items-center gap-2">
            <input
              type="color"
              aria-label={`${label} picker`}
              value={HEX.test(field.value) ? field.value : '#000000'}
              disabled={disabled}
              onChange={(e) => field.onChange(e.target.value)}
              className="h-9 w-11 shrink-0 cursor-pointer rounded-md border border-border bg-card p-1"
            />
            <FormControl>
              <Input className="font-mono" maxLength={7} disabled={disabled} {...field} />
            </FormControl>
          </div>
          {hint && <FormDescription>{hint}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
